import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  Modal,
  Table,
  TableColumns,
} from '@bp/ui-components';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { graphql } from '../../../client/gql';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { SubjectForm } from '../../../components/SubjectEditForm/SubjectForm';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { NodeWithColor } from '../../../utils/colorHelper';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';

const DeleteBpSubjectMutationDocument = graphql(`
  mutation DeleteBpSubject($uuid: ID!) {
    deleteBpSubject(uuid: $uuid) {
      entitiesDeleted
    }
  }
`);

const InstitutionSubjectsSubpageQueryDocument = graphql(`
  query InstitutionSubjectsSubpageData($where: SubjectWhere!) {
    subjects(where: $where) {
      uuid
      __typename
      name
      managedBy
      globalSubject
      color
      shortName
    }
  }
`);

export type SubjectType = {
  uuid: string;
  name: string;
  managedBy: string;
  globalSubject?: string | null;
  color?: string | null | undefined;
  shortName: string;
};

export const InstitutionSubjectsSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const isDesktop = useIsDesktop();

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('subjects.title', { count: 1 }),
      context: 'male',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const [{ data: subjectsData }] = useQuery({
    query: InstitutionSubjectsSubpageQueryDocument,
    variables: {
      where: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
    },
  });

  const perms = usePermissionChecker();

  const subjects = subjectsData?.subjects ?? [];

  const [, deleteSubject] = useMutation(DeleteBpSubjectMutationDocument);

  const [currentSubject, setCurrentSubject] = useState<SubjectType | undefined>();
  const [modalOpen, setModalOpen] = useState(false);

  const tableColumns: TableColumns<SubjectType>[] = [
    {
      header: t('common.designation'),
      id: 'name',
      accessorKey: 'name',
      canExpand: true,
    },
    {
      header: t('common.shortName'),
      id: 'shortName',
      accessorKey: 'shortName',
    },
    {
      header: t('common.color'),
      id: 'color',
      accessorFn: (subject: SubjectType) => ({ color: subject.color }),
      type: 'color',
    },
    {
      header: t('subjects.globalSubject'),
      id: 'globalSubject',
      accessorKey: 'globalSubject',
      size: 400,
    },
    {
      header: t('institution.dataSource'),
      id: 'managedBy',
      accessorKey: 'managedBy',
    },
  ];

  const handleEdit = (subject: SubjectType) => {
    setCurrentSubject(subject);
    setModalOpen(true);
  };

  const handleDelete = async (subject: SubjectType) => {
    if (perms?.canDeleteSubject({ uuid: organizationUuid })) {
      const { error } = await deleteSubject({ uuid: subject.uuid });
      error ? showErrorToast(error) : showSuccessToast(t('subjects.deleted'));
    }
  };

  const dropDownMenu = (subject: SubjectType): DropdownMenuItem[] => {
    return [
      { label: t('common.edit'), onClick: () => handleEdit(subject) },
      {
        label: t('delete.delete'),
        color: 'error',
        onClick: async () => {
          const res = await confirmDelete();
          if (res) {
            await handleDelete(subject);
          }
        },
      },
    ];
  };

  const handleClose = () => {
    setCurrentSubject(undefined);
    setModalOpen(false);
  };

  const colorsInUse = subjects.map((subject) => ({
    color: subject.color ?? null,
    owner: { name: subject.name, type: 'Subject' as NodeWithColor },
  }));

  return (
    <BpSubpage isForbidden={!perms?.canListSubjects({ uuid: organizationUuid })}>
      {pimAuthClaims.getOrganization() && (
        <Table<SubjectType>
          isDesktop={isDesktop}
          data-cy={'groupList'}
          showActionBar
          showBorderRadius
          showShadow
          isOnWhite={false}
          actionBarSettings={{
            showAddButton: perms?.canCreateSubject({ uuid: organizationUuid }),
            showExpertFilter: true,
            addButtonText: t('common.addType', {
              type: t('subjects.title', { count: 1 }),
            }),
          }}
          sorting={[{ id: 'name', desc: false }]}
          onAddClick={() => {
            setModalOpen(true);
          }}
          columns={tableColumns}
          data={subjects}
          lastCol={(row) => {
            return perms?.canDeleteSubject(pimAuthClaims.getOrganization()) ? (
              <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} />}>
                <DropdownMenu data={dropDownMenu(row.original)} />
              </Dropdown>
            ) : null;
          }}
        />
      )}

      <Modal title={t('subjects.settings')} isOpen={modalOpen} onRequestClose={handleClose} width='s'>
        <SubjectForm colorsInUse={colorsInUse} closeModal={handleClose} subject={currentSubject} perms={perms} />
      </Modal>

      <ConfirmationDialog />
    </BpSubpage>
  );
};
